import React from 'react';
import { InboxIcon } from 'lucide-react';

// ── Card ──────────────────────────────────────────────────────────────────────

export function Card({ children, style = {}, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 14,
        padding: 24,
        transition: 'border-color 0.15s, background 0.15s',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────────────

export function StatCard({
  label,
  value,
  subValue,
  trend,
  icon: Icon,
  accentColor = '#0084ff',
  style = {},
}) {
  const isPositive = trend > 0;

  return (
    <Card style={{ position: 'relative', overflow: 'hidden', ...style }}>
      {/* Radial gradient accent */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 90,
        height: 90,
        background: `radial-gradient(circle at top right, ${accentColor}22, transparent 70%)`,
        borderRadius: '0 14px 0 0',
        pointerEvents: 'none',
      }} />

      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
      }}>
        <div>
          <div style={{
            color: 'rgba(255,255,255,0.45)',
            fontSize: 12,
            fontWeight: 500,
            marginBottom: 8,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}>
            {label}
          </div>
          <div style={{
            color: '#fff',
            fontSize: 28,
            fontWeight: 700,
            letterSpacing: '-1px',
            lineHeight: 1.1,
          }}>
            {value}
          </div>
          {subValue && (
            <div style={{
              color: 'rgba(255,255,255,0.4)',
              fontSize: 13,
              marginTop: 6,
            }}>
              {subValue}
            </div>
          )}
        </div>
        {Icon && (
          <div style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            background: `${accentColor}18`,
            border: `1px solid ${accentColor}30`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Icon size={20} color={accentColor} />
          </div>
        )}
      </div>

      {trend !== undefined && trend !== null && (
        <div style={{
          marginTop: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 5,
        }}>
          <span style={{
            color: isPositive ? '#00c896' : '#ff5c5c',
            fontSize: 13,
            fontWeight: 600,
          }}>
            {isPositive ? '\u2191' : '\u2193'} {Math.abs(trend).toFixed(1)}%
          </span>
          <span style={{
            color: 'rgba(255,255,255,0.35)',
            fontSize: 12,
          }}>
            vs last month
          </span>
        </div>
      )}
    </Card>
  );
}

// ── SectionHeader ─────────────────────────────────────────────────────────────

export function SectionHeader({ title, subtitle, actions }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      marginBottom: 22,
    }}>
      <div>
        <h2 style={{
          color: '#fff',
          fontSize: 20,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          margin: 0,
          lineHeight: 1.2,
        }}>
          {title}
        </h2>
        {subtitle && (
          <p style={{
            color: 'rgba(255,255,255,0.4)',
            fontSize: 13,
            margin: '5px 0 0',
          }}>
            {subtitle}
          </p>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────────

const BADGE_COLORS = {
  HIGH:     { bg: 'rgba(255,92,92,0.15)',   text: '#ff5c5c',  border: 'rgba(255,92,92,0.3)' },
  MEDIUM:   { bg: 'rgba(255,184,0,0.15)',   text: '#ffb800',  border: 'rgba(255,184,0,0.3)' },
  LOW:      { bg: 'rgba(0,200,150,0.15)',   text: '#00c896',  border: 'rgba(0,200,150,0.3)' },
  PENDING:  { bg: 'rgba(74,158,255,0.15)',  text: '#4a9eff',  border: 'rgba(74,158,255,0.3)' },
  APPLIED:  { bg: 'rgba(0,200,150,0.15)',   text: '#00c896',  border: 'rgba(0,200,150,0.3)' },
  APPROVED: { bg: 'rgba(255,184,0,0.15)',   text: '#ffb800',  border: 'rgba(255,184,0,0.3)' },
};

export function Badge({ label, type = 'MEDIUM' }) {
  const colors = BADGE_COLORS[type] || BADGE_COLORS.MEDIUM;
  return (
    <span style={{
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 600,
      background: colors.bg,
      color: colors.text,
      border: `1px solid ${colors.border}`,
      letterSpacing: '0.04em',
      display: 'inline-block',
      lineHeight: '16px',
    }}>
      {label}
    </span>
  );
}

// ── Button ────────────────────────────────────────────────────────────────────

const BUTTON_VARIANTS = {
  primary:   { background: '#0084ff', color: '#fff', border: 'none' },
  secondary: { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.1)' },
  danger:    { background: 'rgba(255,92,92,0.15)', color: '#ff5c5c', border: '1px solid rgba(255,92,92,0.3)' },
  success:   { background: 'rgba(0,200,150,0.15)', color: '#00c896', border: '1px solid rgba(0,200,150,0.3)' },
};

const BUTTON_SIZES = {
  sm: { padding: '5px 12px', fontSize: 12 },
  md: { padding: '8px 18px', fontSize: 14 },
  lg: { padding: '12px 24px', fontSize: 15 },
};

export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  style = {},
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        borderRadius: 8,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s ease',
        fontFamily: 'inherit',
        lineHeight: 1.4,
        ...BUTTON_VARIANTS[variant],
        ...BUTTON_SIZES[size],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// ── Loader ────────────────────────────────────────────────────────────────────

export function Loader() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 80,
    }}>
      <div style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.08)',
        borderTopColor: '#0084ff',
        animation: 'smalblu-spin 0.75s linear infinite',
      }} />
      <style>{`@keyframes smalblu-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────

export function EmptyState({ message = 'No data available', icon: Icon = InboxIcon }) {
  return (
    <div style={{
      textAlign: 'center',
      padding: 60,
      color: 'rgba(255,255,255,0.3)',
    }}>
      <div style={{
        width: 48,
        height: 48,
        borderRadius: 12,
        background: 'rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 14px',
      }}>
        <Icon size={24} color="rgba(255,255,255,0.2)" />
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.5 }}>{message}</div>
    </div>
  );
}
